import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Plus, Trash2, Send, ClipboardList, BarChart3, CheckCircle, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';

const API = BACKEND_URL;
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#14B8A6', '#F97316', '#EC4899'];

const PDSurveys = ({ ideaId, surveys: initialSurveys, canManage }) => {
  const [surveys, setSurveys] = useState(initialSurveys || []);
  const [showCreate, setShowCreate] = useState(false);
  const [viewResults, setViewResults] = useState(null);
  const [respondingSurvey, setRespondingSurvey] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [newSurvey, setNewSurvey] = useState({ title: '', description: '', questions: [{ text: '', type: 'single', options: ['', ''] }] });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSurveys = async () => {
    try {
      const res = await axios.get(`${API}/api/participatory-design/ideas/${ideaId}/surveys`, { headers });
      setSurveys(res.data);
    } catch { /* ignore */ }
  };

  const handleCreateSurvey = async () => {
    if (!newSurvey.title) { toast.error('Title required'); return; }
    const questions = newSurvey.questions.filter(q => q.text.trim()).map(q => ({
      ...q, options: (q.type === 'single' || q.type === 'multi') ? q.options.filter(o => o.trim()) : [],
    }));
    if (questions.length === 0) { toast.error('At least 1 question required'); return; }
    try {
      await axios.post(`${API}/api/participatory-design/ideas/${ideaId}/surveys`, { ...newSurvey, questions }, { headers });
      toast.success('Survey created');
      setShowCreate(false);
      setNewSurvey({ title: '', description: '', questions: [{ text: '', type: 'single', options: ['', ''] }] });
      fetchSurveys();
    } catch { toast.error('Failed to create survey'); }
  };

  const handleRespond = async (surveyId) => {
    try {
      await axios.post(`${API}/api/participatory-design/surveys/${surveyId}/respond`, { answers }, { headers });
      toast.success('Response submitted');
      setRespondingSurvey(null);
      setAnswers([]);
      fetchSurveys();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
  };

  const handleViewResults = async (surveyId) => {
    try {
      const res = await axios.get(`${API}/api/participatory-design/surveys/${surveyId}/results`, { headers });
      setViewResults(res.data);
    } catch { toast.error('Failed to load results'); }
  };

  const openRespond = async (surveyId) => {
    try {
      const res = await axios.get(`${API}/api/participatory-design/surveys/${surveyId}`, { headers });
      setRespondingSurvey(res.data);
      setAnswers(res.data.questions.map((_, i) => ({ question_index: i, value: '' })));
    } catch { toast.error('Failed to load survey'); }
  };

  const updateAnswer = (qi, value) => {
    setAnswers(prev => prev.map(a => a.question_index === qi ? { ...a, value } : a));
  };

  const addQuestion = () => setNewSurvey(p => ({ ...p, questions: [...p.questions, { text: '', type: 'single', options: ['', ''] }] }));
  const updateQuestion = (idx, field, value) => {
    setNewSurvey(p => { const q = [...p.questions]; q[idx] = { ...q[idx], [field]: value }; return { ...p, questions: q }; });
  };
  const removeQuestion = (idx) => setNewSurvey(p => ({ ...p, questions: p.questions.filter((_, i) => i !== idx) }));

  return (
    <div className="space-y-3" data-testid="surveys-section">
      {canManage && (
        <Button size="sm" variant="outline" onClick={() => setShowCreate(true)} data-testid="create-survey-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Create Survey</Button>
      )}

      {surveys.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">No surveys yet. Create one to collect community needs data.</p>
      ) : (
        surveys.map(s => (
          <Card key={s.id} data-testid={`survey-${s.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{s.title}</p>
                  {s.description && <p className="text-xs text-gray-500">{s.description}</p>}
                  <div className="flex items-center gap-2 mt-1.5">
                    <Badge variant="outline" className="text-xs">{s.questions?.length || 0} questions</Badge>
                    <Badge className={`text-xs ${s.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{s.status}</Badge>
                    <span className="text-xs text-gray-500">{s.response_count} responses</span>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => handleViewResults(s.id)} data-testid={`results-${s.id}`}>
                    <BarChart3 className="h-3 w-3 mr-1" /> Results
                  </Button>
                  {s.status === 'open' && !s.user_responded && (
                    <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-xs" onClick={() => openRespond(s.id)} data-testid={`respond-${s.id}`}>
                      <Send className="h-3 w-3 mr-1" /> Respond
                    </Button>
                  )}
                  {s.user_responded && <Badge className="bg-green-100 text-green-700 text-xs">Responded</Badge>}
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* Results View */}
      <Dialog open={!!viewResults} onOpenChange={() => setViewResults(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Survey Results: {viewResults?.title}</DialogTitle></DialogHeader>
          {viewResults && (
            <div className="space-y-4 pt-2">
              <Badge variant="outline">{viewResults.total_responses} total responses</Badge>
              {(viewResults.results || []).map((r, ri) => (
                <Card key={ri}>
                  <CardContent className="p-3">
                    <p className="text-sm font-medium text-gray-800 mb-2">Q{ri + 1}: {r.text}</p>
                    {r.type === 'rating' && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl font-bold text-indigo-600">{r.average}</span>
                          <span className="text-xs text-gray-500">/ 5 average ({r.response_count} responses)</span>
                        </div>
                        <ResponsiveContainer width="100%" height={100}>
                          <BarChart data={Object.entries(r.distribution || {}).map(([k, v]) => ({ rating: k, count: v }))}>
                            <XAxis dataKey="rating" fontSize={10} />
                            <YAxis allowDecimals={false} fontSize={10} />
                            <Bar dataKey="count" fill="#6366F1" radius={[3, 3, 0, 0]} barSize={20} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                    {(r.type === 'single' || r.type === 'multi') && (
                      <div className="space-y-1">
                        {Object.entries(r.option_counts || {}).sort((a, b) => b[1] - a[1]).map(([opt, count], i) => {
                          const pct = r.response_count > 0 ? Math.round((count / r.response_count) * 100) : 0;
                          return (
                            <div key={opt} className="relative p-2 rounded bg-gray-50 overflow-hidden">
                              <div className="absolute inset-0 bg-indigo-100" style={{ width: `${pct}%` }} />
                              <div className="relative flex justify-between text-xs">
                                <span className="text-gray-800">{opt}</span>
                                <span className="font-medium">{pct}% ({count})</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {r.type === 'text' && (
                      <div className="space-y-1">
                        {(r.text_answers || []).map((a, ai) => (
                          <div key={ai} className="p-2 bg-gray-50 rounded text-xs text-gray-700 italic">"{a}"</div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Respond Dialog */}
      <Dialog open={!!respondingSurvey} onOpenChange={() => setRespondingSurvey(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{respondingSurvey?.title}</DialogTitle></DialogHeader>
          {respondingSurvey && (
            <div className="space-y-4 pt-2">
              {respondingSurvey.description && <p className="text-xs text-gray-500">{respondingSurvey.description}</p>}
              {(respondingSurvey.questions || []).map((q, qi) => (
                <div key={qi} className="border rounded-lg p-3">
                  <Label className="text-sm">{q.text}</Label>
                  {q.type === 'text' && (
                    <Textarea value={answers[qi]?.value || ''} onChange={e => updateAnswer(qi, e.target.value)} rows={2} className="mt-1 text-sm" placeholder="Your answer..." data-testid={`answer-${qi}`} />
                  )}
                  {q.type === 'rating' && (
                    <div className="flex gap-1 mt-2">
                      {[1, 2, 3, 4, 5].map(n => (
                        <button key={n} onClick={() => updateAnswer(qi, n)}
                          className={`w-10 h-10 rounded-full text-sm font-medium ${answers[qi]?.value === n ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          data-testid={`rating-${qi}-${n}`}>{n}</button>
                      ))}
                    </div>
                  )}
                  {q.type === 'single' && (
                    <div className="space-y-1 mt-2">
                      {(q.options || []).map((opt, oi) => (
                        <button key={oi} onClick={() => updateAnswer(qi, opt)}
                          className={`w-full text-left p-2 rounded border text-sm ${answers[qi]?.value === opt ? 'bg-indigo-50 border-indigo-300' : 'hover:bg-gray-50'}`}>
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                  {q.type === 'multi' && (
                    <div className="space-y-1 mt-2">
                      {(q.options || []).map((opt, oi) => {
                        const sel = Array.isArray(answers[qi]?.value) ? answers[qi].value : [];
                        const isSelected = sel.includes(opt);
                        return (
                          <button key={oi} onClick={() => updateAnswer(qi, isSelected ? sel.filter(x => x !== opt) : [...sel, opt])}
                            className={`w-full text-left p-2 rounded border text-sm ${isSelected ? 'bg-indigo-50 border-indigo-300' : 'hover:bg-gray-50'}`}>
                            <span className={`inline-block w-4 h-4 rounded mr-2 border ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`} />
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setRespondingSurvey(null)}>Cancel</Button>
                <Button onClick={() => handleRespond(respondingSurvey.id)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-response-btn">Submit Response</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Survey Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Survey / Needs Assessment</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><Label>Title *</Label><Input value={newSurvey.title} onChange={e => setNewSurvey({ ...newSurvey, title: e.target.value })} placeholder="e.g., Community Needs Assessment" data-testid="survey-title-input" /></div>
            <div><Label>Description</Label><Input value={newSurvey.description} onChange={e => setNewSurvey({ ...newSurvey, description: e.target.value })} placeholder="Context for respondents..." /></div>
            <div className="flex items-center justify-between"><Label className="font-semibold">Questions</Label><Button size="sm" variant="ghost" onClick={addQuestion}><Plus className="h-3 w-3 mr-1" /> Add Question</Button></div>
            {newSurvey.questions.map((q, qi) => (
              <Card key={qi} className="bg-gray-50/50">
                <CardContent className="p-3 space-y-2">
                  <div className="flex gap-2 items-start">
                    <span className="text-xs text-gray-500 mt-2 font-medium">Q{qi + 1}</span>
                    <div className="flex-1 space-y-2">
                      <Input value={q.text} onChange={e => updateQuestion(qi, 'text', e.target.value)} placeholder="Question text..." className="h-8 text-sm" />
                      <Select value={q.type} onValueChange={v => updateQuestion(qi, 'type', v)}>
                        <SelectTrigger className="h-7 text-xs w-36"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Free Text</SelectItem>
                          <SelectItem value="rating">Rating (1-5)</SelectItem>
                          <SelectItem value="single">Single Choice</SelectItem>
                          <SelectItem value="multi">Multiple Choice</SelectItem>
                        </SelectContent>
                      </Select>
                      {(q.type === 'single' || q.type === 'multi') && (
                        <div className="space-y-1">
                          {(q.options || []).map((opt, oi) => (
                            <div key={oi} className="flex gap-1">
                              <Input value={opt} onChange={e => { const opts = [...q.options]; opts[oi] = e.target.value; updateQuestion(qi, 'options', opts); }} placeholder={`Option ${oi + 1}`} className="h-7 text-xs" />
                              {q.options.length > 2 && <Button size="sm" variant="ghost" className="text-red-400 h-7" onClick={() => updateQuestion(qi, 'options', q.options.filter((_, j) => j !== oi))}><Trash2 className="h-2.5 w-2.5" /></Button>}
                            </div>
                          ))}
                          <Button size="sm" variant="ghost" className="text-xs h-6" onClick={() => updateQuestion(qi, 'options', [...(q.options || []), ''])}><Plus className="h-2.5 w-2.5 mr-0.5" /> Option</Button>
                        </div>
                      )}
                    </div>
                    {newSurvey.questions.length > 1 && <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeQuestion(qi)}><Trash2 className="h-3 w-3" /></Button>}
                  </div>
                </CardContent>
              </Card>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreateSurvey} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-survey-btn">Create Survey</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PDSurveys;
